import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import ReactDOM from 'react-dom/client';
import { Constants } from './Constants';
import './main.scss';
import { AuthConfig, AuthHelper } from './libs/auth/AuthHelper';
import { store } from './redux/app/store';
import { Provider as ReduxProvider } from 'react-redux';
import React from 'react';
import { BackendServiceUrl } from './libs/services/BaseService';
import { setAuthConfig } from './redux/features/app/appSlice';
import { App } from './App';
import CacheUtils from './libs/utils/CacheUtils';
import { app as teamsApp, authentication as teamsAuth } from '@microsoft/teams-js';

if (!localStorage.getItem('debug')) {
    localStorage.setItem('debug', `${Constants.debug.root}:*`);
}

let container: HTMLElement | null = null;
let root: ReactDOM.Root | undefined = undefined;
let msalInstance: PublicClientApplication | undefined;

document.addEventListener('DOMContentLoaded', () => {
    if (!container) {
        container = document.getElementById('root');

        if (!container) {
            throw new Error('Could not find root element');
        }

        root = ReactDOM.createRoot(container);

        renderApp();
    }
});

const renderAuthApp = async (authConfig: AuthConfig) => {
    if (AuthHelper.isAuthAAD()) {
        if (!msalInstance) {
            msalInstance = new PublicClientApplication(AuthHelper.getMsalConfig(authConfig));

            const isTeamsInit = teamsApp.isInitialized();
            await msalInstance.initialize();

            void msalInstance.handleRedirectPromise().then((response) => {
                if (response) {
                    msalInstance?.setActiveAccount(response.account);

                    if (isTeamsInit) {
                        teamsAuth.notifySuccess();
                    }
                }
            }).catch((e) => {
                if (isTeamsInit) {
                    teamsAuth.notifyFailure(e instanceof Error ? e.message : String(e));
                }
            });
        }

        // render with the MsalProvider if AAD is enabled
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        root!.render(
            <React.StrictMode>
                <ReduxProvider store={store}>
                    <MsalProvider instance={msalInstance}>
                        <App />
                    </MsalProvider>
                </ReduxProvider>
            </React.StrictMode>,
        );
    }
};

export const renderApp = async () => {
    void await teamsApp.initialize().catch((e: unknown) => {
        console.error('Teams init failed:', e);
    });

    const dataCacheKey = 'authConfig';
    const cachedData = CacheUtils.getStoredDataByKey(dataCacheKey, true);

    if (cachedData) {
        store.dispatch(setAuthConfig(cachedData));
        void renderAuthApp(cachedData);
    } else {
        fetch(new URL('authConfig', BackendServiceUrl))
            .then((response) => (response.ok ? (response.json() as Promise<AuthConfig>) : Promise.reject()))
            .then(async (authConfig) => {
                CacheUtils.setStoredDataByKey(dataCacheKey, authConfig, CacheUtils.dataCacheWorkWeekExpiresMin, true);
                store.dispatch(setAuthConfig(authConfig));
                await renderAuthApp(authConfig);
            })
            .catch(() => {
                store.dispatch(setAuthConfig(undefined));
            });
    }
};
